import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  completeOnboarding,
  getUserProfile,
  saveOnboardingProgress,
} from '../api/dataService';
import { useAuth } from '../context/AuthContext';
import {
  EXERCISE_FAMILIARITY_OPTIONS,
  FITNESS_ASSESSMENT_DOMAINS,
  FITNESS_ASSESSMENT_DIFFICULTY_STYLES,
  createInitialAssessmentAnswers,
  scoreFitnessAssessment,
} from '../utils/fitnessAssessment';
import {
  ACTIVITY_OPTIONS,
  DIETARY_OPTIONS,
  EQUIPMENT_CHIPS,
  GENDER_OPTIONS,
  WEEKDAY_OPTIONS,
  WORKOUT_OPTIONS,
  buildOnboardingProfilePayload,
  convertLengthInputToMetric as convertLengthValueToMetric,
  convertWeightInputToMetric as convertWeightValueToMetric,
  ensureArray,
  getLengthDisplayValue as getLengthValueForUnits,
  getWeightDisplayValue as getWeightValueForUnits,
  getWorkoutTargetOptions,
  hydrateProfile,
  initialProfileFormState,
  normalizePreferredWorkoutDays,
  normalizeWeeklyWorkoutTarget,
  toggleListValue,
} from '../utils/onboardingProfile';
import { useLocale } from '../context/LocaleContext';

const getNativeCurrentInstallPushStatus = () => {
  try {
    return window?.__FORZA_NATIVE_PUSH_STATUS__ || null;
  } catch (error) {
    return null;
  }
};

const DETAILED_STEP_SEQUENCE = [
  'personal-info',
  'fitness-profile',
  'body-composition',
  'health-details',
  'preferences',
  'review',
  'assessment',
];

const FAST_STEP_SEQUENCE = [
  'fast-basics',
  'fast-goal',
  'fast-familiarity',
  'fast-availability',
  'fast-equipment',
  'fast-health',
  'fast-nutrition',
  'fast-review',
  'assessment',
];

const getStepMetadata = (t) => ({
  'personal-info': { title: t('onboarding.steps.personalInfo', 'Create Your Profile'), icon: 'badge' },
  'fitness-profile': { title: t('onboarding.steps.fitnessProfile', 'Training Context'), icon: 'monitoring' },
  'body-composition': { title: t('onboarding.steps.bodyComposition', 'Body Measurements'), icon: 'straighten' },
  'health-details': { title: t('onboarding.steps.healthDetails', 'Health Essentials'), icon: 'health_and_safety' },
  preferences: { title: t('onboarding.steps.preferences', 'App Preferences'), icon: 'tune' },
  review: { title: t('onboarding.steps.review', 'Review & Confirm'), icon: 'rule' },
  assessment: { title: t('onboarding.steps.assessment', 'Fitness Assessment'), icon: 'speed' },
  'fast-basics': { title: t('onboarding.steps.fastBasics', 'Basics'), icon: 'badge' },
  'fast-goal': { title: t('onboarding.steps.fastGoal', 'Goal'), icon: 'flag' },
  'fast-familiarity': { title: t('onboarding.steps.fastFamiliarity', 'Exercise Familiarity'), icon: 'school' },
  'fast-availability': { title: t('onboarding.steps.fastAvailability', 'Weekly Availability'), icon: 'calendar_month' },
  'fast-equipment': { title: t('onboarding.steps.fastEquipment', 'Equipment'), icon: 'fitness_center' },
  'fast-health': { title: t('onboarding.steps.fastHealth', 'Health Notes'), icon: 'health_and_safety' },
  'fast-nutrition': { title: t('onboarding.steps.fastNutrition', 'Nutrition Notes'), icon: 'restaurant' },
  'fast-review': { title: t('onboarding.steps.fastReview', 'Review'), icon: 'rule' },
});

const initialFormState = initialProfileFormState;

const formReducer = (state, action) => {
  switch (action.type) {
    case 'replace':
      return action.payload;
    case 'updateSection':
      return {
        ...state,
        [action.section]: {
          ...state[action.section],
          ...action.payload,
        },
      };
    default:
      return state;
  }
};

export const resolveOnboardingResumeState = (progress) => {
  const savedMode = ['fast', 'detailed'].includes(progress?.mode) ? progress.mode : null;
  if (!savedMode) {
    return { mode: null, stepIndex: 0 };
  }

  const savedSequence = savedMode === 'fast' ? FAST_STEP_SEQUENCE : DETAILED_STEP_SEQUENCE;
  const savedIndex = typeof progress?.lastStep === 'string'
    ? savedSequence.indexOf(progress.lastStep)
    : -1;

  return {
    mode: savedMode,
    stepIndex: savedIndex >= 0 ? Math.min(savedIndex + 1, savedSequence.length - 1) : 0,
  };
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { t } = useLocale();

  const [formState, dispatch] = useReducer(formReducer, initialFormState);
  const [mode, setMode] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [progressSaving, setProgressSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [isPushNotificationsPending, setIsPushNotificationsPending] = useState(false);
  const [showPushSettingsButton, setShowPushSettingsButton] = useState(false);
  const [hasAllergies, setHasAllergies] = useState(false);
  const [equipmentEntry, setEquipmentEntry] = useState('');
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [assessmentAnswers, setAssessmentAnswers] = useState(createInitialAssessmentAnswers);

  const stepSequence = mode === 'fast' ? FAST_STEP_SEQUENCE : DETAILED_STEP_SEQUENCE;
  const currentStepId = mode ? stepSequence[stepIndex] : 'mode-choice';
  const stepMetadata = useMemo(() => getStepMetadata(t), [t]);
  const currentMeta = stepMetadata[currentStepId] || { title: t('onboarding.steps.chooseSetup', 'Choose Setup'), icon: 'route' };
  const preferredUnits = formState.equipmentAndPreferences.preferredUnits;
  const progressPercent = mode
    ? Math.round((stepIndex / Math.max(1, stepSequence.length - 1)) * 100)
    : 0;

  const assessmentScore = useMemo(() => scoreFitnessAssessment(assessmentAnswers), [assessmentAnswers]);
  const targetOptions = getWorkoutTargetOptions(formState.healthAndFitnessDetails.activityFrequency);
  const weeklyWorkoutTarget = normalizeWeeklyWorkoutTarget(
    formState.healthAndFitnessDetails.weeklyWorkoutTarget,
    formState.healthAndFitnessDetails.activityFrequency
  );
  const preferredWorkoutDays = normalizePreferredWorkoutDays(formState.healthAndFitnessDetails.preferredWorkoutDays);
  const isDetailedFitnessProfile = mode === 'detailed' && currentStepId === 'fitness-profile';
  const isDetailedWorkoutScheduleValid = !isDetailedFitnessProfile
    || (targetOptions.length > 0 && preferredWorkoutDays.length === weeklyWorkoutTarget);

  const convertLengthInputToMetric = (raw) => {
    return convertLengthValueToMetric(raw, preferredUnits);
  };

  const convertWeightInputToMetric = (raw) => {
    return convertWeightValueToMetric(raw, preferredUnits);
  };

  const getLengthDisplayValue = (stored, decimalsImperial = 1, decimalsMetric = 0) => {
    return getLengthValueForUnits(stored, preferredUnits, decimalsImperial, decimalsMetric);
  };

  const getWeightDisplayValue = (stored) => {
    return getWeightValueForUnits(stored, preferredUnits);
  };

  const optionLabel = useCallback((group, value, fallback = value) => (
    t(`onboarding.options.${group}.${value}`, fallback)
  ), [t]);

  const translatedActivityOptions = useMemo(() => (
    ACTIVITY_OPTIONS.map((option) => ({
      ...option,
      label: optionLabel('activity', option.value, option.label),
    }))
  ), [optionLabel]);

  const translatedWeekdayOptions = useMemo(() => (
    WEEKDAY_OPTIONS.map((day) => ({
      ...day,
      label: optionLabel('weekdays', day.label, day.label),
    }))
  ), [optionLabel]);

  const translatedFamiliarityOptions = useMemo(() => (
    EXERCISE_FAMILIARITY_OPTIONS.map((option) => ({
      ...option,
      label: optionLabel('familiarity', option.value, option.label),
      description: t(
        `onboarding.options.familiarityDescriptions.${option.value}`,
        option.description
      ),
    }))
  ), [optionLabel, t]);

  const getAssessmentDomainLabel = useCallback((domain) => (
    t(`onboarding.assessmentDomains.${domain.id}.label`, domain.label)
  ), [t]);

  const getAssessmentDomainPrompt = useCallback((domain) => (
    t(`onboarding.assessmentDomains.${domain.id}.prompt`, domain.prompt)
  ), [t]);

  const getAssessmentVariationLabel = useCallback((domain, variation) => (
    t(
      `onboarding.assessmentDomains.${domain.id}.variations.${variation.value}`,
      variation.label
    )
  ), [t]);

  const getAssessmentVariationDescription = useCallback((domain, variation) => (
    t(
      `onboarding.assessmentDomains.${domain.id}.variationDescriptions.${variation.value}`,
      ''
    )
  ), [t]);

  const getAssessmentDifficultyLabel = useCallback((variation) => (
    t(
      `onboarding.assessmentDifficulty.${variation.value}`,
      variation.difficultyLabel || variation.value
    )
  ), [t]);

  const getAssessmentUnitLabel = useCallback((unit) => (
    t(`onboarding.assessmentUnits.${unit}`, unit)
  ), [t]);

  const measurementUnitShort = preferredUnits === 'imperial' ? 'in' : 'cm';
  const weightUnitLabel = preferredUnits === 'imperial' ? 'lbs' : 'kg';

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      try {
        const { data } = await getUserProfile();
        if (!isMounted) return;
        const profile = hydrateProfile(data?.profileData?.profile);
        dispatch({ type: 'replace', payload: profile });
        const nativePushStatus = getNativeCurrentInstallPushStatus();
        setPushNotificationsEnabled(Boolean(nativePushStatus?.enabled ?? data?.enablePushNotifications));
        setHasAllergies(profile.additionalInformation.allergies !== 'None');

        const resumeState = resolveOnboardingResumeState(data?.profileData?.onboardingProgress);
        if (resumeState.mode) {
          setMode(resumeState.mode);
          setStepIndex(resumeState.stepIndex);
        }
      } catch (error) {
        if (!isMounted) return;
        setGlobalError(error?.response?.data?.message || t('onboarding.errors.loadProfile', 'Failed to load your profile. Please refresh and try again.'));
      } finally {
        if (isMounted) setLoadingProfile(false);
      }
    };

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleNativeEvent = (event) => {
      const payload = event?.detail;
      if (!payload || payload.type !== 'FORZA_PUSH_NOTIFICATIONS_RESULT') {
        return;
      }

      setIsPushNotificationsPending(false);

      if (payload.user) {
        updateUser(payload.user);
      }

      const nextPushStatus = payload.currentInstallPushNotificationStatus || payload.user?.pushNotificationStatus || null;

      if (payload.status === 'enabled' || payload.status === 'synced') {
        setPushNotificationsEnabled(Boolean(nextPushStatus?.enabled ?? true));
        setShowPushSettingsButton(false);
        setGlobalError('');
        return;
      }

      if (payload.status === 'disabled' || payload.status === 'disabled_external' || payload.status === 'disabled_local') {
        setPushNotificationsEnabled(false);
        if (payload.status === 'disabled_external') {
          setShowPushSettingsButton(true);
          setGlobalError(t('onboarding.errors.pushDisabledExternal', 'Push notifications were disabled in iPhone Settings. Enable them there if you want reminders from Forza.'));
        } else {
          setShowPushSettingsButton(false);
          setGlobalError('');
        }
        return;
      }

      if (payload.status === 'denied') {
        setPushNotificationsEnabled(false);
        setShowPushSettingsButton(true);
        setGlobalError(t('onboarding.errors.pushDenied', 'Notifications are disabled in iPhone Settings. Turn them on there, then try again.'));
        return;
      }

      if (payload.status === 'error') {
        setPushNotificationsEnabled(false);
        setShowPushSettingsButton(Boolean(payload.canOpenSettings));
        setGlobalError(payload.error || t('onboarding.errors.pushConfig', 'Failed to configure push notifications.'));
      }
    };

    window.addEventListener('forza:native', handleNativeEvent);
    return () => window.removeEventListener('forza:native', handleNativeEvent);
  }, [updateUser]);

  const updateSection = (section, payload) => {
    dispatch({ type: 'updateSection', section, payload });
  };

  const handlePushNotificationsToggle = useCallback(() => {
    setGlobalError('');
    setShowPushSettingsButton(false);

    const isNativeApp = Boolean(window?.forzaNative?.setPushNotificationsEnabled);
    if (!isNativeApp) {
      setPushNotificationsEnabled((prev) => !prev);
      return;
    }

    setIsPushNotificationsPending(true);
    window.forzaNative.setPushNotificationsEnabled(!pushNotificationsEnabled);
  }, [pushNotificationsEnabled]);

  const handleOpenNativeSettings = useCallback(() => {
    if (!window?.forzaNative?.openAppSettings) {
      return;
    }
    window.forzaNative.openAppSettings();
  }, []);

  const selectActivityFrequency = (activityFrequency, { includeWorkoutDaySelection = false } = {}) => {
    const nextTarget = includeWorkoutDaySelection
      ? normalizeWeeklyWorkoutTarget(
        formState.healthAndFitnessDetails.weeklyWorkoutTarget,
        activityFrequency
      )
      : '';
    updateSection('healthAndFitnessDetails', {
      activityFrequency,
      weeklyWorkoutTarget: nextTarget,
      preferredWorkoutDays: includeWorkoutDaySelection
        ? preferredWorkoutDays.slice(0, Number(nextTarget) || 0)
        : [],
    });
  };

  const selectWeeklyWorkoutTarget = (target) => {
    updateSection('healthAndFitnessDetails', {
      weeklyWorkoutTarget: target,
      preferredWorkoutDays: preferredWorkoutDays.slice(0, target),
    });
  };

  const togglePreferredWorkoutDay = (day) => {
    const selected = preferredWorkoutDays.includes(day);
    const nextDays = selected
      ? preferredWorkoutDays.filter((item) => item !== day)
      : [...preferredWorkoutDays, day].slice(0, Number(weeklyWorkoutTarget) || 0);
    updateSection('healthAndFitnessDetails', { preferredWorkoutDays: nextDays });
  };

  const persistStep = async (stepId) => {
    setProgressSaving(true);
    setGlobalError('');
    try {
      const profilePayload = buildOnboardingProfilePayload(formState, { hasAllergies });
      await saveOnboardingProgress(stepId, profilePayload, mode);
    } catch (error) {
      setGlobalError(error?.response?.data?.message || t('onboarding.errors.saveProgress', 'Failed to save onboarding progress.'));
      throw error;
    } finally {
      setProgressSaving(false);
    }
  };

  const moveNext = async () => {
    if (!mode || progressSaving || isConfirming) return;
    const stepId = currentStepId;
    if (stepId === 'assessment') return;
    if (!isDetailedWorkoutScheduleValid) {
      setGlobalError(
        t(
          'onboarding.errors.workoutDaysExact',
          'Choose exactly {count} workout day{suffix} before continuing.',
          {
            count: weeklyWorkoutTarget || 'the selected number of',
            suffix: weeklyWorkoutTarget === 1 ? '' : 's',
          }
        )
      );
      return;
    }
    try {
      await persistStep(stepId);
      setStepIndex((prev) => Math.min(prev + 1, stepSequence.length - 1));
    } catch {
      // error is rendered via globalError
    }
  };

  const moveBack = () => {
    if (progressSaving || isConfirming) return;
    setGlobalError('');
    if (stepIndex === 0) {
      setMode(null);
      return;
    }
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const completeFlow = async ({ fitnessAssessmentDecision, includeAssessment = false }) => {
    if (isConfirming) return;
    setIsConfirming(true);
    setGlobalError('');
    try {
      await persistStep(currentStepId);
      const payload = {
        onboardingMode: mode,
        fitnessAssessmentDecision,
        safetyAcknowledged: true,
      };
      if (includeAssessment) {
        payload.fitnessAssessment = {
          answers: assessmentScore.answers,
        };
      }
      const response = await completeOnboarding(payload);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      if (response?.data?.user) {
        updateUser(response.data.user);
      }
      navigate('/dashboard', { replace: true, state: { isGenerating: true } });
    } catch (error) {
      setGlobalError(error?.response?.data?.message || t('onboarding.errors.finalize', 'Failed to finalise onboarding. Please try again.'));
    } finally {
      setIsConfirming(false);
    }
  };

  const addEquipment = () => {
    const additions = equipmentEntry
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (!additions.length) {
      setEquipmentEntry('');
      return;
    }
    const merged = Array.from(new Set([...ensureArray(formState.equipmentAndPreferences.availableEquipment), ...additions]));
    updateSection('equipmentAndPreferences', { availableEquipment: merged });
    setEquipmentEntry('');
  };

  const chipButton = ({ label, active, onClick }) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${active
        ? 'border-cyan-400 bg-cyan-400/20 text-cyan-100'
        : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/50'
        }`}
    >
      {label}
    </button>
  );

  const renderBasicsFields = () => (
    <div className="grid gap-5 md:grid-cols-2">
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.fullName', 'Full name')}</span>
        <input
          type="text"
          value={formState.personalInformation.fullName}
          onChange={(event) => updateSection('personalInformation', { fullName: event.target.value })}
          placeholder={t('onboarding.enterFullName', 'Enter your full name')}
          className="input-shell"
        />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.age', 'Age')}</span>
        <input
          type="number"
          value={formState.personalInformation.age}
          onChange={(event) => updateSection('personalInformation', { age: event.target.value })}
          placeholder={t('onboarding.enterAge', 'Enter your age')}
          className="input-shell"
        />
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.height', 'Height')}</span>
        <div className="relative">
          <input
            type="number"
            value={getLengthDisplayValue(formState.personalInformation.heightCm, 1, 0)}
            onChange={(event) => updateSection('personalInformation', { heightCm: convertLengthInputToMetric(event.target.value) })}
            placeholder={t('onboarding.enterHeight', 'Enter your height')}
            className="input-shell pr-12"
            inputMode="decimal"
            step="any"
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{measurementUnitShort}</span>
        </div>
      </label>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.weight', 'Weight')}</span>
        <div className="relative">
          <input
            type="number"
            value={getWeightDisplayValue(formState.personalInformation.weightKg)}
            onChange={(event) => updateSection('personalInformation', { weightKg: convertWeightInputToMetric(event.target.value) })}
            placeholder={t('onboarding.enterWeight', 'Enter your weight')}
            className="input-shell pr-12"
            inputMode="decimal"
            step="any"
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{weightUnitLabel}</span>
        </div>
      </label>
      <div>
        <span className="text-sm font-medium text-slate-200">{t('onboarding.gender', 'Gender')}</span>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {GENDER_OPTIONS.map((option) => chipButton({
            label: optionLabel('gender', option, option),
            active: formState.personalInformation.sex === option,
            onClick: () => updateSection('personalInformation', { sex: option }),
          }))}
        </div>
      </div>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.location', 'Location')}</span>
        <input
          type="text"
          value={formState.personalInformation.location}
          onChange={(event) => updateSection('personalInformation', { location: event.target.value })}
          placeholder={t('onboarding.cityCountry', 'City, Country')}
          className="input-shell"
        />
      </label>
    </div>
  );

  const renderModeChoice = () => (
    <div className="mx-auto flex min-h-[70vh] max-w-5xl flex-col justify-center px-4 py-12">
      <div className="mb-8 max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">{t('onboarding.setupTag', 'Forza setup')}</p>
        <h1 className="mt-3 text-4xl font-bold text-white sm:text-5xl">{t('onboarding.modeChoiceTitle', 'Choose how much setup you want now.')}</h1>
        <p className="mt-4 text-base text-slate-400">
          {t(
            'onboarding.modeChoiceDescription',
            'Fast setup gets you to your first generated quest plan with the essentials. Detailed setup gives the planner more context upfront.'
          )}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <button
          type="button"
          onClick={() => {
            setMode('fast');
            setStepIndex(0);
          }}
          className="rounded-3xl border border-cyan-400/50 bg-cyan-400/10 p-6 text-left transition hover:bg-cyan-400/15"
        >
          <span className="material-symbols-outlined text-4xl text-cyan-300">chat</span>
          <h2 className="mt-5 text-2xl font-bold text-white">{t('onboarding.fastTitle', 'Fast setup')}</h2>
          <p className="mt-2 text-sm text-slate-300">
            {t(
              'onboarding.fastDescription',
              'A structured coach chat that captures profile basics, goals, training context, health notes, and nutrition constraints.'
            )}
          </p>
          <p className="mt-5 text-sm font-semibold text-cyan-200">{t('onboarding.fastBestFor', 'Best for reaching value quickly.')}</p>
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('detailed');
            setStepIndex(0);
          }}
          className="rounded-3xl border border-slate-700 bg-slate-900/80 p-6 text-left transition hover:border-cyan-400/50"
        >
          <span className="material-symbols-outlined text-4xl text-slate-300">view_list</span>
          <h2 className="mt-5 text-2xl font-bold text-white">{t('onboarding.detailedTitle', 'Detailed setup')}</h2>
          <p className="mt-2 text-sm text-slate-400">
            {t(
              'onboarding.detailedDescription',
              'The full profile flow with body measurements, workout style details, and app preferences. Measurement units default to metric and can be changed later in Settings.'
            )}
          </p>
          <p className="mt-5 text-sm font-semibold text-slate-200">{t('onboarding.detailedBestFor', 'Best for richer personalization.')}</p>
        </button>
      </div>
    </div>
  );

  const renderExerciseFamiliarity = () => (
    <div className="grid gap-3">
      {translatedFamiliarityOptions.map((option) => {
        const active = formState.healthAndFitnessDetails.exerciseFamiliarity === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => updateSection('healthAndFitnessDetails', { exerciseFamiliarity: option.value })}
            className={`rounded-2xl border p-4 text-left transition-colors ${active
              ? 'border-cyan-400 bg-cyan-400/15'
              : 'border-slate-700 bg-slate-900/70 hover:border-cyan-400/50'
              }`}
          >
            <p className="text-sm font-bold text-white">{option.label}</p>
            <p className="mt-1 text-xs text-slate-400">{option.description}</p>
          </button>
        );
      })}
    </div>
  );

  const renderWorkoutDaySelector = () => {
    if (!formState.healthAndFitnessDetails.activityFrequency) {
      return null;
    }

    return (
      <div className="space-y-5">
        <div>
          <span className="text-sm font-semibold text-slate-200">{t('onboarding.sessionsPerWeek', 'Sessions per week')}</span>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            {targetOptions.map((target) => chipButton({
              label: target.toString(),
              active: weeklyWorkoutTarget === target,
              onClick: () => selectWeeklyWorkoutTarget(target),
            }))}
          </div>
        </div>
        <div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <span className="text-sm font-semibold text-slate-200">{t('onboarding.preferredWorkoutDays', 'Preferred workout days')}</span>
            <span className="text-xs font-medium text-slate-500">
              {t('onboarding.preferredWorkoutDaysCount', '{selected}/{total} selected', {
                selected: preferredWorkoutDays.length,
                total: weeklyWorkoutTarget || 0,
              })}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3 sm:grid-cols-7">
            {translatedWeekdayOptions.map((day) => {
              const active = preferredWorkoutDays.includes(day.value);
              const disabled = !active && preferredWorkoutDays.length >= Number(weeklyWorkoutTarget || 0);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => togglePreferredWorkoutDay(day.value)}
                  disabled={disabled}
                  className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${active
                    ? 'border-cyan-400 bg-cyan-400/20 text-cyan-100'
                    : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-500/50 disabled:cursor-not-allowed disabled:opacity-40'
                    }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {t(
              'onboarding.workoutDayHint',
              'The planner will generate workout quests only on these weekdays. Meals and mindfulness still appear every day.'
            )}
          </p>
        </div>
      </div>
    );
  };

  const renderDetailedStep = () => {
    switch (currentStepId) {
      case 'personal-info':
        return (
          <section className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-white">{t('onboarding.tellUsAboutYourself', 'Tell us about yourself')}</h2>
              <p className="mt-1 text-sm text-slate-400">{t('onboarding.basicsDescription', 'These basics help us tune nutrition, scheduling, and leaderboard context.')}</p>
            </div>
            {renderBasicsFields()}
          </section>
        );
      case 'fitness-profile':
        return (
          <section className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold text-white">{t('onboarding.trainingContextTitle', 'Training context')}</h2>
              <p className="mt-1 text-sm text-slate-400">{t('onboarding.trainingContextDescription', 'Exercise familiarity controls movement complexity. The optional fitness test handles starting level.')}</p>
            </div>
            {renderExerciseFamiliarity()}
            <div>
              <span className="text-sm font-semibold text-slate-200">{t('onboarding.weeklyActivity', 'Weekly activity')}</span>
              <div className="mt-3 grid gap-3 sm:grid-cols-4">
                {translatedActivityOptions.map((option) => chipButton({
                  label: option.label,
                  active: formState.healthAndFitnessDetails.activityFrequency === option.value,
                  onClick: () => selectActivityFrequency(option.value, { includeWorkoutDaySelection: true }),
                }))}
              </div>
            </div>
            {renderWorkoutDaySelector()}
            <label className="block">
              <span className="text-sm font-semibold text-slate-200">{t('onboarding.fitnessGoals', 'What are your fitness goals?')}</span>
              <textarea
                rows={4}
                value={formState.healthAndFitnessDetails.fitnessGoals}
                onChange={(event) => updateSection('healthAndFitnessDetails', { fitnessGoals: event.target.value })}
                placeholder={t('onboarding.fitnessGoalsPlaceholder', 'e.g., Build strength, lose fat, feel athletic again...')}
                className="input-shell mt-3 min-h-[120px]"
              />
            </label>
          </section>
        );
      case 'body-composition':
        return renderBodyComposition();
      case 'health-details':
        return renderHealthDetails();
      case 'preferences':
        return renderPreferences();
      case 'review':
        return renderReview();
      case 'assessment':
        return renderAssessmentStep();
      default:
        return null;
    }
  };

  const renderFastStep = () => {
    const botLine = (text) => (
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-cyan-400 text-slate-950">
          <span className="material-symbols-outlined text-lg">smart_toy</span>
        </div>
        <div className="rounded-2xl rounded-tl-sm bg-slate-800 px-4 py-3 text-sm text-slate-100">{text}</div>
      </div>
    );

    const answerShell = (content) => (
      <div className="ml-0 mt-5 rounded-3xl border border-slate-800 bg-slate-950/50 p-5 sm:ml-12">{content}</div>
    );

    switch (currentStepId) {
      case 'fast-basics':
        return (
          <section>
            {botLine(t('onboarding.fastCoachIntro', "I’ll keep this short. First, I need your basic profile so the plan has usable context."))}
            {answerShell(renderBasicsFields())}
          </section>
        );
      case 'fast-goal':
        return (
          <section>
            {botLine(t('onboarding.fastGoalPrompt', 'What outcome should the first quest plan optimize for? Write it in your own words.'))}
            {answerShell(
              <textarea
                rows={5}
                value={formState.healthAndFitnessDetails.fitnessGoals}
                onChange={(event) => updateSection('healthAndFitnessDetails', { fitnessGoals: event.target.value })}
                placeholder={t('onboarding.fastGoalPlaceholder', 'e.g., I want to lose fat without losing strength, train 4 days a week, and improve consistency.')}
                className="input-shell min-h-[140px]"
              />
            )}
          </section>
        );
      case 'fast-familiarity':
        return (
          <section>
            {botLine(t('onboarding.fastFamiliarityPrompt', 'How familiar are you with exercise technique and movement variations?'))}
            {answerShell(renderExerciseFamiliarity())}
          </section>
        );
      case 'fast-availability':
        return (
          <section>
            {botLine(t('onboarding.fastAvailabilityPrompt', 'How many days per week can you realistically train or complete wellness quests?'))}
            {answerShell(
              <div className="grid gap-3 sm:grid-cols-4">
                {translatedActivityOptions.map((option) => chipButton({
                  label: option.label,
                  active: formState.healthAndFitnessDetails.activityFrequency === option.value,
                  onClick: () => selectActivityFrequency(option.value),
                }))}
              </div>
            )}
          </section>
        );
      case 'fast-equipment':
        return (
          <section>
            {botLine(t('onboarding.fastEquipmentPrompt', 'What equipment can the planner assume you have access to?'))}
            {answerShell(renderEquipmentSelector())}
          </section>
        );
      case 'fast-health':
        return (
          <section>
            {botLine(t('onboarding.fastHealthPrompt', 'Any injuries, medical conditions, pain, or movements the plan should respect?'))}
            {answerShell(
              <div className="space-y-3">
                <textarea
                  rows={4}
                  value={formState.additionalInformation.medicalConditions}
                  onChange={(event) => updateSection('additionalInformation', { medicalConditions: event.target.value })}
                  placeholder={t('onboarding.fastHealthPlaceholder', 'Write None if there is nothing to flag.')}
                  className="input-shell min-h-[110px]"
                />
                <p className="text-xs text-amber-200">{t('onboarding.fastHealthHint', 'If you report a concern, Forza will still continue but your plan should stay conservative around it.')}</p>
              </div>
            )}
          </section>
        );
      case 'fast-nutrition':
        return (
          <section>
            {botLine(t('onboarding.fastNutritionPrompt', 'Last one: any dietary preferences or allergies that meal quests must respect?'))}
            {answerShell(renderNutritionSelector())}
          </section>
        );
      case 'fast-review':
        return renderReview();
      case 'assessment':
        return renderAssessmentStep();
      default:
        return null;
    }
  };

  const renderBodyComposition = () => (
    <section className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">{t('onboarding.bodyTitle', 'Measure your frame')}</h2>
        <p className="mt-1 text-sm text-slate-400">{t('onboarding.bodyDescription', 'All circumference fields are optional. You can leave every field blank and continue to the next onboarding step.')}</p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {[
          ['neckCm', t('onboarding.bodyLabels.neck', 'Neck')],
          ['chestCm', t('onboarding.bodyLabels.chest', 'Chest')],
          ['waistCm', t('onboarding.bodyLabels.waist', 'Waist')],
          ['hipsCm', t('onboarding.bodyLabels.hips', 'Hips')],
          ['thighCm', t('onboarding.bodyLabels.thigh', 'Thigh')],
        ].map(([key, label]) => (
          <label key={key} className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-200">{label}</span>
            <div className="relative">
              <input
                type="number"
                value={getLengthDisplayValue(formState.bodyComposition[key])}
                onChange={(event) => updateSection('bodyComposition', { [key]: convertLengthInputToMetric(event.target.value) })}
                placeholder={t('onboarding.bodyLabels.circumference', '{label} circumference', { label })}
                className="input-shell pr-12"
                inputMode="decimal"
                step="any"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{measurementUnitShort}</span>
            </div>
          </label>
        ))}
      </div>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-200">{t('onboarding.optionalNotes', 'Optional notes')}</span>
        <textarea
          rows={3}
          value={formState.bodyComposition.notes}
          onChange={(event) => updateSection('bodyComposition', { notes: event.target.value })}
          placeholder={t('onboarding.optionalNotesPlaceholder', 'Any asymmetry, current focus, or measurement context.')}
          className="input-shell min-h-[100px]"
        />
      </label>
    </section>
  );

  const renderNutritionSelector = () => (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-semibold text-slate-200">{t('onboarding.dietaryPreferences', 'Dietary preferences')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {DIETARY_OPTIONS.map((option) => chipButton({
            label: optionLabel('dietary', option, option),
            active: formState.additionalInformation.dietaryPreferences.includes(option),
            onClick: () => updateSection('additionalInformation', {
              dietaryPreferences: toggleListValue(formState.additionalInformation.dietaryPreferences, option),
            }),
          }))}
        </div>
      </div>
      <div className="space-y-3">
        <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3">
          <span className="text-sm font-medium text-slate-200">{t('onboarding.allergiesToggle', 'I have allergies')}</span>
          <input
            type="checkbox"
            checked={hasAllergies}
            onChange={() => {
              const next = !hasAllergies;
              setHasAllergies(next);
              updateSection('additionalInformation', { allergies: next ? '' : 'None' });
            }}
            className="size-5 accent-cyan-500"
          />
        </label>
        {hasAllergies && (
          <textarea
            rows={3}
            value={formState.additionalInformation.allergies === 'None' ? '' : formState.additionalInformation.allergies}
            onChange={(event) => updateSection('additionalInformation', { allergies: event.target.value })}
            placeholder={t('onboarding.allergiesPlaceholder', 'List allergies or reactions.')}
            className="input-shell min-h-[100px]"
          />
        )}
      </div>
    </div>
  );

  const renderHealthDetails = () => (
    <section className="space-y-8">
      <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5">
        <p className="text-sm font-semibold text-amber-100">{t('onboarding.safetyNote', 'Safety note')}</p>
        <p className="mt-1 text-xs text-amber-100/80">{t('onboarding.safetyDescription', 'Reported limitations will be passed to the planner. This is not medical clearance.')}</p>
      </div>
      <label className="block">
        <span className="text-sm font-semibold text-slate-200">{t('onboarding.medicalConditions', 'Medical conditions, injuries, or pain')}</span>
        <textarea
          rows={4}
          value={formState.additionalInformation.medicalConditions}
          onChange={(event) => updateSection('additionalInformation', { medicalConditions: event.target.value })}
          placeholder={t('onboarding.medicalConditionsPlaceholder', 'e.g., Asthma, knee pain, previous shoulder injury, or None.')}
          className="input-shell mt-3 min-h-[120px]"
        />
      </label>
      {renderNutritionSelector()}
    </section>
  );

  const renderEquipmentSelector = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {EQUIPMENT_CHIPS.map((option) => chipButton({
          label: optionLabel('equipment', option, option),
          active: formState.equipmentAndPreferences.availableEquipment.includes(option),
          onClick: () => updateSection('equipmentAndPreferences', {
            availableEquipment: toggleListValue(formState.equipmentAndPreferences.availableEquipment, option),
          }),
        }))}
      </div>
      <div className="flex gap-3">
        <input
          type="text"
          value={equipmentEntry}
          onChange={(event) => setEquipmentEntry(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addEquipment();
            }
          }}
          placeholder={t('onboarding.addCustomEquipment', 'Add custom equipment')}
          className="input-shell"
        />
        <button type="button" onClick={addEquipment} className="rounded-xl bg-cyan-500 px-5 py-3 text-sm font-bold text-slate-950">{t('onboarding.add', 'Add')}</button>
      </div>
    </div>
  );

  const renderPreferences = () => (
    <section className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white">{t('onboarding.preferencesTitle', 'Tailor the experience')}</h2>
        <p className="mt-1 text-sm text-slate-400">{t('onboarding.preferencesDescription', 'Detailed setup includes workout styles and push notifications.')}</p>
      </div>
      <label className="flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
        <div>
          <p className="text-sm font-semibold text-slate-200">{t('onboarding.pushNotifications', 'Push notifications')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('onboarding.pushNotificationsDescription', 'Receive reminders and quest updates.')}</p>
        </div>
        <input
          type="checkbox"
          checked={pushNotificationsEnabled}
          onChange={handlePushNotificationsToggle}
          disabled={isPushNotificationsPending}
          className="size-5 accent-cyan-500"
        />
      </label>
      {showPushSettingsButton && Boolean(window?.forzaNative?.openAppSettings) && (
        <button
          type="button"
          onClick={handleOpenNativeSettings}
          className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-200"
        >
          {t('onboarding.openIphoneSettings', 'Open iPhone Settings')}
        </button>
      )}
      <div>
        <p className="text-sm font-semibold text-slate-200">{t('onboarding.favouriteWorkoutStyles', 'Favourite workout styles')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {WORKOUT_OPTIONS.map((option) => chipButton({
            label: optionLabel('workout', option, option),
            active: formState.healthAndFitnessDetails.workoutPreferences.includes(option),
            onClick: () => updateSection('healthAndFitnessDetails', {
              workoutPreferences: toggleListValue(formState.healthAndFitnessDetails.workoutPreferences, option),
            }),
          }))}
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-200">{t('onboarding.equipmentYouOwn', 'Equipment you own')}</p>
        <div className="mt-3">{renderEquipmentSelector()}</div>
      </div>
    </section>
  );

  const renderReview = () => {
    const payload = buildOnboardingProfilePayload(formState, { hasAllergies });
    const selectedWorkoutDayLabels = translatedWeekdayOptions
      .filter((day) => payload.healthAndFitnessDetails.preferredWorkoutDays.includes(day.value))
      .map((day) => day.label)
      .join(', ');
    const row = (label, value) => (
      <p className="text-sm text-slate-300"><span className="text-slate-500">{label}:</span> {value || t('onboarding.notProvided', 'Not provided')}</p>
    );

    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white">{t('onboarding.reviewTitle', 'Review your setup')}</h2>
          <p className="mt-1 text-sm text-slate-400">{t('onboarding.reviewDescription', 'Next, you can complete or skip a one-time fitness assessment before quests are generated.')}</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="summary-panel">
            <h3 className="summary-title">{t('onboarding.reviewProfile', 'Profile')}</h3>
            {row(t('onboarding.name', 'Name'), payload.personalInformation.fullName)}
            {row(t('onboarding.age', 'Age'), payload.personalInformation.age)}
            {row(t('onboarding.gender', 'Gender'), optionLabel('gender', payload.personalInformation.sex, payload.personalInformation.sex))}
            {row(t('onboarding.location', 'Location'), payload.personalInformation.location)}
            {row(t('onboarding.height', 'Height'), payload.personalInformation.heightCm ? `${getLengthDisplayValue(payload.personalInformation.heightCm)} ${measurementUnitShort}` : '')}
            {row(t('onboarding.weight', 'Weight'), payload.personalInformation.weightKg ? `${getWeightDisplayValue(payload.personalInformation.weightKg)} ${weightUnitLabel}` : '')}
          </div>
          <div className="summary-panel">
            <h3 className="summary-title">{t('onboarding.reviewTraining', 'Training')}</h3>
            {row(t('onboarding.goal', 'Goal'), payload.healthAndFitnessDetails.fitnessGoals)}
            {row(t('onboarding.familiarity', 'Familiarity'), optionLabel('familiarity', payload.healthAndFitnessDetails.exerciseFamiliarity, payload.healthAndFitnessDetails.exerciseFamiliarity))}
            {row(t('onboarding.weeklyActivity', 'Weekly activity'), optionLabel('activity', payload.healthAndFitnessDetails.activityFrequency, payload.healthAndFitnessDetails.activityFrequency))}
            {row(t('onboarding.workoutDays', 'Workout days'), selectedWorkoutDayLabels)}
            {row(
              t('onboarding.workoutStyles', 'Workout styles'),
              payload.healthAndFitnessDetails.workoutPreferences.map((item) => optionLabel('workout', item, item)).join(', ')
            )}
          </div>
          <div className="summary-panel">
            <h3 className="summary-title">{t('onboarding.reviewEquipment', 'Equipment')}</h3>
            {row(
              t('onboarding.units', 'Units'),
              optionLabel(
                'units',
                payload.equipmentAndPreferences.preferredUnits === 'imperial' ? 'Imperial (lbs, miles)' : 'Metric (kg, km)',
                payload.equipmentAndPreferences.preferredUnits === 'imperial' ? 'Imperial (lbs, miles)' : 'Metric (kg, km)'
              )
            )}
            {row(
              t('onboarding.availableEquipment', 'Available equipment'),
              payload.equipmentAndPreferences.availableEquipment.map((item) => optionLabel('equipment', item, item)).join(', ')
            )}
          </div>
          <div className="summary-panel">
            <h3 className="summary-title">{t('onboarding.reviewHealth', 'Health & nutrition')}</h3>
            {row(t('onboarding.healthNotes', 'Health notes'), payload.additionalInformation.medicalConditions)}
            {row(
              t('onboarding.dietaryPreferences', 'Dietary preferences'),
              payload.additionalInformation.dietaryPreferences.map((item) => optionLabel('dietary', item, item)).join(', ') || t('onboarding.none', 'None')
            )}
            {row(t('onboarding.allergiesToggle', 'Allergies'), payload.additionalInformation.allergies)}
          </div>
        </div>
      </section>
    );
  };

  const renderAssessmentStep = () => {
    if (!assessmentStarted) {
      return (
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-white">{t('onboarding.assessmentTitle', 'Optional fitness assessment')}</h2>
            <p className="mt-1 text-sm text-slate-400">
              {t('onboarding.assessmentDescription', 'Complete a short self-report test to calibrate your first quest plan. Skip it to start conservatively and do it later from Settings.')}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <button type="button" onClick={() => setAssessmentStarted(true)} className="rounded-3xl border border-cyan-400/50 bg-cyan-400/10 p-6 text-left">
              <span className="material-symbols-outlined text-4xl text-cyan-300">speed</span>
              <h3 className="mt-4 text-xl font-bold text-white">{t('onboarding.startAssessment', 'Start assessment')}</h3>
              <p className="mt-2 text-sm text-slate-300">{t('onboarding.startAssessmentDescription', 'Pick exercise variations and enter your result for six home-friendly domains.')}</p>
            </button>
            <button type="button" onClick={() => completeFlow({ fitnessAssessmentDecision: 'skipped' })} className="rounded-3xl border border-slate-700 bg-slate-900 p-6 text-left">
              <span className="material-symbols-outlined text-4xl text-slate-300">skip_next</span>
              <h3 className="mt-4 text-xl font-bold text-white">{t('onboarding.skipForNow', 'Skip for now')}</h3>
              <p className="mt-2 text-sm text-slate-400">{t('onboarding.skipForNowDescription', 'Generate conservative starter quests now and keep the assessment available in Settings.')}</p>
            </button>
          </div>
        </section>
      );
    }

    return (
      <section className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white">{t('onboarding.selfReportTitle', 'Self-report fitness test')}</h2>
          <p className="mt-1 text-sm text-slate-400">
            {t('onboarding.selfReportDescription', 'Choose the hardest variation you can perform with clean control, then enter your result where requested. We calculate the baseline privately.')}
          </p>
        </div>
        <div className="grid gap-4">
          {FITNESS_ASSESSMENT_DOMAINS.map((domain) => {
            const selectedVariation = assessmentAnswers[domain.id]?.variation;
            const hasResultInput = domain.resultInputType !== 'none';
            return (
              <div key={domain.id} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                <div>
                  <h3 className="text-base font-bold text-white">{getAssessmentDomainLabel(domain)}</h3>
                  <p className="mt-1 text-xs text-slate-500">{getAssessmentDomainPrompt(domain)}</p>
                </div>
                <div className={hasResultInput ? 'mt-4 grid gap-3 lg:grid-cols-[1fr_180px]' : 'mt-4'}>
                  <div className={`grid gap-2 ${hasResultInput ? 'sm:grid-cols-3' : 'sm:grid-cols-5'}`}>
                    {domain.variations.map((variation) => {
                      const active = selectedVariation === variation.value;
                      const style = FITNESS_ASSESSMENT_DIFFICULTY_STYLES[variation.value] || FITNESS_ASSESSMENT_DIFFICULTY_STYLES.standard;
                      return (
                        <button
                          key={variation.value}
                          type="button"
                          onClick={() => setAssessmentAnswers((prev) => ({
                            ...prev,
                            [domain.id]: { ...prev[domain.id], variation: variation.value },
                          }))}
                          className={`rounded-2xl border p-3 text-left transition ${active
                            ? style.className
                            : 'border-slate-700 bg-slate-950/50 text-slate-300 hover:border-slate-500'
                            }`}
                        >
                          <span className="text-[10px] font-black uppercase tracking-[0.22em]">{getAssessmentDifficultyLabel(variation)}</span>
                          <span className="mt-1 block text-sm font-bold">{getAssessmentVariationLabel(domain, variation)}</span>
                          {getAssessmentVariationDescription(domain, variation) ? (
                            <span className="mt-2 block text-xs font-medium leading-5 text-slate-300">
                              {getAssessmentVariationDescription(domain, variation)}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  {hasResultInput && (
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        value={assessmentAnswers[domain.id]?.result}
                        onChange={(event) => setAssessmentAnswers((prev) => ({
                          ...prev,
                          [domain.id]: { ...prev[domain.id], result: event.target.value },
                        }))}
                        placeholder={t('onboarding.resultPlaceholder', 'Result')}
                        className="input-shell pr-20"
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">{getAssessmentUnitLabel(domain.unit)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100">
          {t('onboarding.assessmentSafety', 'This is self-reported and should be performed only if you feel safe doing so. Stop if anything feels wrong.')}
        </div>
      </section>
    );
  };

  if (loadingProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <p className="text-sm text-slate-400">{t('onboarding.loading', 'Loading your onboarding profile...')}</p>
      </div>
    );
  }

  if (!mode) {
    return <div className="min-h-screen bg-[#040b16] text-slate-100">{renderModeChoice()}</div>;
  }

  const renderMainContent = mode === 'fast' ? renderFastStep : renderDetailedStep;
  const isAssessment = currentStepId === 'assessment';
  const isReview = currentStepId === 'review' || currentStepId === 'fast-review';

  return (
    <div className="relative min-h-screen bg-[#040b16] text-slate-100">
      <style>{`
        .input-shell {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(51 65 85);
          background: rgba(15, 23, 42, 0.7);
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: white;
          outline: none;
        }
        .input-shell::placeholder { color: rgb(100 116 139); }
        .input-shell:focus {
          border-color: rgb(34 211 238);
          box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.28);
        }
        .summary-panel {
          border-radius: 1rem;
          border: 1px solid rgb(30 41 59);
          background: rgba(15, 23, 42, 0.7);
          padding: 1.25rem;
        }
        .summary-title {
          margin-bottom: 0.75rem;
          font-size: 1rem;
          font-weight: 700;
          color: white;
        }
        @media (max-width: 639px) {
          .input-shell {
            font-size: 16px;
          }
        }
      `}</style>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 lg:flex-row lg:py-16">
        <aside className="hidden w-full shrink-0 rounded-3xl border border-slate-800 bg-slate-900/70 p-6 lg:block lg:w-[320px]">
          <button type="button" onClick={() => setMode(null)} className="mb-6 text-sm font-semibold text-cyan-300 hover:text-cyan-200">
            {t('onboarding.changePath', 'Change setup path')}
          </button>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300">
              <span className="material-symbols-outlined">stars</span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Forza</p>
              <h1 className="text-lg font-semibold text-white">{mode === 'fast' ? t('onboarding.fastTitle', 'Fast setup') : t('onboarding.detailedTitle', 'Detailed setup')}</h1>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{t('onboarding.progress', 'Progress')}</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-cyan-400 transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
          <div className="mt-6 space-y-2">
            {stepSequence.map((stepKey, index) => {
              const meta = stepMetadata[stepKey];
              const active = index === stepIndex;
              return (
                <div key={`${stepKey}-${index}`} className={`rounded-2xl border px-3 py-3 ${active ? 'border-cyan-400 bg-cyan-400/15' : 'border-slate-800 bg-slate-900/60'}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('onboarding.step', 'Step {current}', { current: index + 1 })}</p>
                  <p className="mt-1 text-sm font-medium text-white">{meta.title}</p>
                </div>
              );
            })}
          </div>
        </aside>

        <section className="flex min-h-[70vh] flex-1 flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/80">
          <header className="border-b border-slate-800 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex size-11 items-center justify-center rounded-2xl bg-slate-950 text-cyan-300">
                <span className="material-symbols-outlined">{currentMeta.icon}</span>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">{t('onboarding.stepOf', 'Step {current} of {total}', { current: stepIndex + 1, total: stepSequence.length })}</p>
                <h1 className="text-lg font-semibold text-white">{currentMeta.title}</h1>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-6 py-8">
            {globalError && (
              <div className="mb-5 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {globalError}
              </div>
            )}
            {renderMainContent()}
          </main>

          <footer className="border-t border-slate-800 px-6 py-5">
            {isAssessment && assessmentStarted ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setAssessmentStarted(false)} className="rounded-2xl border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300">
                  {t('onboarding.back', 'Back')}
                </button>
                <button
                  type="button"
                  onClick={() => completeFlow({ fitnessAssessmentDecision: 'submitted', includeAssessment: true })}
                  disabled={isConfirming}
                  className="rounded-2xl bg-cyan-500 px-6 py-3 text-sm font-bold text-slate-950 disabled:opacity-60"
                >
                  {isConfirming ? t('onboarding.generatingQuests', 'Generating quests...') : t('onboarding.submitAssessment', 'Submit assessment & generate quests')}
                </button>
              </div>
            ) : !isAssessment ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={moveNext}
                  disabled={progressSaving || !isDetailedWorkoutScheduleValid}
                  className="rounded-2xl bg-cyan-500 px-6 py-3 text-sm font-bold text-slate-950 disabled:opacity-60"
                >
                  {progressSaving ? t('onboarding.saving', 'Saving...') : isReview ? t('onboarding.continueToAssessment', 'Continue to assessment') : t('onboarding.next', 'Next')}
                </button>
                <button type="button" onClick={moveBack} disabled={progressSaving} className="rounded-2xl border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300 disabled:opacity-40">
                  {t('onboarding.back', 'Back')}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={moveBack}
                  disabled={progressSaving || isConfirming}
                  className="rounded-2xl border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300 disabled:opacity-40"
                >
                  {t('onboarding.back', 'Back')}
                </button>
              </div>
            )}
          </footer>
        </section>
      </div>
    </div>
  );
};

export default OnboardingPage;
